/**
 * /constitution/proposals — pending change proposals + approvals matrix.
 *
 * Each row: proposer, summary, target (rule / mission / principle /
 * strategy), workflow status. Approve / Reject buttons are role-gated:
 *   - founder may approve everything
 *   - admin may approve non-constitution proposals
 *   - others see read-only
 *
 * Approve calls the upstream activate endpoint; reject marks the row
 * superseded/archived in the canonical table. The list polls every
 * 5 s while a proposal is in the pending or in_review state.
 */
import type { ChangeProposal, ProposalKind } from '@chaos/types/constitution';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Check, FileText, Loader2, Scale, X } from 'lucide-react';
import * as React from 'react';

import { ProposalStatusBadge } from '../../../components/constitution/proposal-status-badge';
import { Button } from '../../../components/ui/button';
import { useToaster } from '../../../components/ui/toast';
import { constitutionApi, constitutionKeys } from '../../../lib/api/constitution';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth-store';

const KIND_LABEL: Record<ProposalKind, string> = {
  constitution_rule: 'Constitution',
  mission: 'Mission',
  principle: 'Principle',
  strategy: 'Strategy',
  goal: 'Goal',
};

function ProposalsRoute() {
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const role =
    workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role ?? null;
  const canApproveAny = role === 'founder' || role === 'admin';
  const canApproveConstitution = role === 'founder';

  const [filter, setFilter] = React.useState<'pending' | 'decided' | 'all'>('pending');

  const proposalsQ = useQuery({
    queryKey: constitutionKeys.proposals(filter === 'all' ? {} : { status: filter }),
    queryFn: () => constitutionApi.listProposals(filter === 'all' ? {} : { status: filter }),
    staleTime: 5_000,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return 30_000;
      const stillPending = data.proposals.some(
        (p) => p.status === 'pending_founder_approval' || p.status === 'in_review',
      );
      return stillPending ? 5_000 : 30_000;
    },
  });

  const proposals: ChangeProposal[] = proposalsQ.data?.proposals ?? [];

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-6">
      <header className="flex flex-wrap items-baseline gap-3">
        <Scale className="h-6 w-6 text-foreground" aria-hidden />
        <div className="flex flex-col">
          <h1 className="text-[18px] font-semibold tracking-tight text-foreground">Proposals</h1>
          <p className="text-[12px] text-muted-foreground">
            Pending governance change proposals from across CHAOS.
          </p>
        </div>
        <FilterChips value={filter} onChange={setFilter} />
        {proposalsQ.isFetching ? (
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> syncing…
          </span>
        ) : null}
      </header>

      <div className="rounded border border-border bg-background">
        <table className="w-full text-[12px]">
          <thead className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Kind</th>
              <th className="px-3 py-2 text-left">Title</th>
              <th className="px-3 py-2 text-left">Proposer</th>
              <th className="px-3 py-2 text-left">Submitted</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {proposals.length === 0 && !proposalsQ.isLoading ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                  No {filter === 'all' ? '' : filter} proposals.
                </td>
              </tr>
            ) : null}
            {proposals.map((p) => (
              <ProposalRow
                key={p.proposalId}
                proposal={p}
                canApproveAny={canApproveAny}
                canApproveConstitution={canApproveConstitution}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface ProposalRowProps {
  proposal: ChangeProposal;
  canApproveAny: boolean;
  canApproveConstitution: boolean;
}

function ProposalRow({ proposal, canApproveAny, canApproveConstitution }: ProposalRowProps) {
  const qc = useQueryClient();
  const { toast } = useToaster();
  const approve = useMutation({
    mutationFn: () => constitutionApi.approveProposal(proposal.proposalId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: constitutionKeys.all });
      toast({ title: 'Proposal approved', description: proposal.title });
    },
    onError: (err: Error) => {
      toast({ title: 'Approve failed', description: err.message, variant: 'destructive' });
    },
  });
  const reject = useMutation({
    mutationFn: () => constitutionApi.rejectProposal(proposal.proposalId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: constitutionKeys.all });
      toast({ title: 'Proposal rejected', description: proposal.title });
    },
    onError: (err: Error) => {
      toast({ title: 'Reject failed', description: err.message, variant: 'destructive' });
    },
  });

  const isPending =
    proposal.status === 'pending_founder_approval' || proposal.status === 'in_review';
  const allowAction =
    isPending && (proposal.kind === 'constitution_rule' ? canApproveConstitution : canApproveAny);

  return (
    <tr className="hover:bg-foreground/5">
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-1 rounded border border-border bg-muted/30 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          <FileText className="h-3 w-3" aria-hidden />
          {KIND_LABEL[proposal.kind]}
        </span>
      </td>
      <td className="px-3 py-2 text-foreground">
        <div className="flex flex-col">
          <span className="font-medium">{proposal.title}</span>
          {proposal.summary ? (
            <span className="line-clamp-1 text-[11px] text-muted-foreground">
              {proposal.summary}
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-3 py-2 text-[11px] text-muted-foreground">
        {proposal.proposerEmail ?? proposal.proposerUserId ?? '—'}
      </td>
      <td className="px-3 py-2 font-mono text-[11px] tabular-nums text-muted-foreground">
        {new Date(proposal.createdAt).toISOString().slice(0, 10)}
      </td>
      <td className="px-3 py-2">
        <ProposalStatusBadge status={proposal.status} />
      </td>
      <td className="px-3 py-2 text-right">
        {allowAction ? (
          <div className="inline-flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => approve.mutate()}
              disabled={approve.isPending || reject.isPending}
              aria-label="Approve"
              className={cn('h-7 px-2 text-success-foreground hover:bg-success/10')}
            >
              {approve.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Check className="h-3.5 w-3.5" aria-hidden />
              )}
              <span className="ml-1 text-[11px]">Approve</span>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => reject.mutate()}
              disabled={approve.isPending || reject.isPending}
              aria-label="Reject"
              className={cn('h-7 px-2 text-destructive-foreground hover:bg-destructive/10')}
            >
              {reject.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <X className="h-3.5 w-3.5" aria-hidden />
              )}
              <span className="ml-1 text-[11px]">Reject</span>
            </Button>
          </div>
        ) : (
          <span className="text-[10px] text-muted-foreground/60">—</span>
        )}
      </td>
    </tr>
  );
}

function FilterChips({
  value,
  onChange,
}: {
  value: 'pending' | 'decided' | 'all';
  onChange: (v: 'pending' | 'decided' | 'all') => void;
}) {
  const options: Array<{ key: typeof value; label: string }> = [
    { key: 'pending', label: 'Pending' },
    { key: 'decided', label: 'Decided' },
    { key: 'all', label: 'All' },
  ];
  return (
    <div className="ml-3 flex items-center gap-0.5 rounded border border-border bg-background p-0.5 text-[11px]">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          aria-pressed={value === o.key}
          onClick={() => onChange(o.key)}
          className={cn(
            'rounded px-2 py-0.5 transition-colors',
            value === o.key
              ? 'bg-foreground/10 text-foreground'
              : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/constitution/proposals')({
  component: ProposalsRoute,
});
